import { Injectable } from '@angular/core';
import {
  map,
  Observable,
  retry,
  Subject,
  switchMap,
  takeUntil,
  timer,
} from 'rxjs';
import {
  AutoFormRole,
  CreateLicenseServerRequestBody,
  ExternalIdPConfiguration,
  PutCidrRangesRequestBody,
  RequestCarBodyDeploymentRequestBody,
} from '../../../../api-client/openapi/models';
import { ControlLicenseServerAction } from '../../../../api-client/openapi/models/control-license-server-action';
import { Customer } from '../../../../api-client/openapi/models/customer';
import { CustomerId } from '../../../../api-client/openapi/models/customer-id';
import { GetCustomerResponse } from '../../../../api-client/openapi/models/get-customer-response';
import { LicenseServer } from '../../../../api-client/openapi/models/license-server';
import { PostIdpConfigRequestBody } from '../../../../api-client/openapi/models/post-idp-config-request-body';
import { PostIdpConfigResponse } from '../../../../api-client/openapi/models/post-idp-config-response';
import { CustomersService, LicenseServersService, UsersService } from '../../../../api-client/openapi/services';

@Injectable({
  providedIn: 'root',
})
export class CustomerService {
  constructor(private customersApiService: CustomersService, private licenseServersApiService: LicenseServersService, private usersApiService: UsersService) {}

  public createCustomerAccount(customer: Customer): Observable<Customer> {
    return this.customersApiService.v1CustomersPut({
      body: {
        customer,
      },
    });
  }

  public addIdpConfig(
    body: PostIdpConfigRequestBody,
    customerId: CustomerId
  ): Observable<PostIdpConfigResponse> {
    return this.customersApiService.v1CustomersCustomerIdIdpConfigPost({
      body,
      customerId,
    });
  }

  public removeIdpConfig(customerId: CustomerId): Observable<{}> {
    return this.customersApiService.v1CustomersCustomerIdIdpConfigDelete({
      customerId,
    });
  }

  public getCustomers(): Observable<Array<Customer>> {
    return this.customersApiService.v1CustomersGet().pipe(map((i) => i.customers));
  }

  public getCustomerById(id: CustomerId): Observable<Customer> {
    return this.customersApiService
      .v1CustomersCustomerIdGet({
        customerId: id,
      })
      .pipe(map((i) => i.customer));
  }

  public getCustomerVisibleRoles(id:CustomerId): Observable<Array<AutoFormRole> >
  {
    return this.customersApiService
      .v1CustomersCustomerIdRolesGet({
        customerId: id,
      })
      .pipe(map((i) => i.roles));
  }

  public getCustomerIdLicenseServer(id: CustomerId): Observable<LicenseServer> {
    return this.licenseServersApiService
      .v1CustomersCustomerIdLicenseServerGet({
        customerId: id,
      })
      .pipe(map((i) => i.licenseServer));
  }

  public updateCustomer(customer: Customer): Observable<GetCustomerResponse> {
    return this.customersApiService.v1CustomersCustomerIdPatch({
      body: {
        name: customer.name,
        appStreamEnabled: customer.appStreamEnabled,
        owner: {
          email: customer.owner.email,
          name: customer.owner.name,
        },
        allowLocalAclsAccess: customer.allowLocalAclsAccess,
      },
      customerId: customer.id,
    });
  }

  public getCustomerIdLicenseServerOptFileUploadUrl(id: CustomerId) {
    return this.licenseServersApiService
      .v1CustomersCustomerIdLicenseServerOptFileUploadGet({
        customerId: id,
      })
      .pipe(map((response) => response.url));
  }

  public updateCidrRanges(
    body: PutCidrRangesRequestBody,
    customerId: CustomerId
  ) {
    return this.licenseServersApiService.v1CustomersCustomerIdLicenseServerCidrRangesPut({
      body,
      customerId,
    });
  }

  public createLicenseServer(
    body: CreateLicenseServerRequestBody,
    id: CustomerId
  ) {
    return this.licenseServersApiService.v1CustomersCustomerIdLicenseServerCreatePost({
      body,
      customerId: id,
    });
  }

  public resetMfa(customerId: CustomerId, userId: string) {
    return this.usersApiService.v1CustomersCustomerIdUsersUserIdResetMfaPost({
      customerId,
      userId,
    });
  }

  public deleteLicenseServer(id: CustomerId) {
    return this.licenseServersApiService.v1CustomersCustomerIdLicenseServerDelete({
      customerId: id,
    });
  }

  public controlLicenseServer(
    id: CustomerId,
    action: ControlLicenseServerAction
  ) {
    return this.licenseServersApiService.v1CustomersCustomerIdLicenseServerControlPost({
      customerId: id,
      body: {
        action: action,
      },
    });
  }

  public getLicenseServerApplicationUrl(id: CustomerId) {
    return this.getCustomerIdLicenseServer(id).pipe(
      map((response) => {
        return response.licenseServerApplicationUrl;
      })
    );
  }

  public getLogFileUrl(id: CustomerId) {
    return this.licenseServersApiService.v1CustomersCustomerIdLicenseServerLogFilesGet({
      customerId: id,
    });
  }

  public getHostIdFileUrl(id: CustomerId) {
    return this.licenseServersApiService.v1CustomersCustomerIdLicenseServerHostIdFileGet({
      customerId: id,
    });
  }

  public getDownloadOptFileUrl(id: CustomerId) {
    return this.licenseServersApiService.v1CustomersCustomerIdLicenseServerOptFileDownloadGet(
      {
        customerId: id,
      }
    );
  }

  public addTemporaryIngress(customerId: CustomerId) {
    return this.licenseServersApiService.v1CustomersCustomerIdLicenseServerAddTemporaryIngressPost(
      { customerId }
    );
  }

  public getLicenseServerStatus(id: CustomerId) {
    return this.getCustomerIdLicenseServer(id).pipe(
      map((response) => {
        return response.status;
      })
    );
  }

  public getLicenseServerCidrRanges(id: CustomerId) {
    return this.getCustomerIdLicenseServer(id).pipe(
      map((response) => {
        return response.cidrRanges;
      })
    );
  }

  public pollServer(
    id: CustomerId,
    stopPolling = new Subject<void>().asObservable()
  ): Observable<LicenseServer> {
    return timer(1, 5000).pipe(
      switchMap(() => this.getCustomerIdLicenseServer(id)),
      map((response) => {
        return response;
      }),
      retry(3),
      takeUntil(stopPolling)
    );
  }

  public resendCustomersPassword(id: CustomerId) {
    return this.customersApiService.v1CustomersCustomerIdResendOwnerPasswordPost({
      customerId: id,
    });
  }

  public deleteCustomer(customerId: CustomerId) {
    return this.customersApiService.v1CustomersCustomerIdDelete({ customerId });
  }

  public requestCarBodyDeployment(
    customerId: CustomerId,
    body: RequestCarBodyDeploymentRequestBody
  ) {
    return this.customersApiService.v1CustomersCustomerIdCarBodyRequestPost({
      customerId,
      body,
    });
  }

  public getAssignedIdPConfiguration(): Observable<ExternalIdPConfiguration> {
    return this.customersApiService.v1CustomersMeIdpConfigGet();
  }
}
